import React, { useState } from "react";
import type { PortalPatientProfile } from "../../types/shared";
import NotificationBell from "./NotificationBell";
import ParseoLogo from "../../shared/ui/ParseoLogo";

interface NavItem {
  id: string;
  label: string;
  icon: string;
}

const NAV_ITEMS: NavItem[] = [
  { id: "profile", label: "Profile", icon: "👤" },
  { id: "chart", label: "Clinical Chart", icon: "📋" },
  { id: "forms", label: "Form Tracker", icon: "📄" },
];

interface PatientPortalLayoutProps {
  children?: React.ReactNode;
  patientId?: string;
  profile?: PortalPatientProfile | null;
  activeSection?: string;
  onSectionChange?: (sectionId: string) => void;
  onLogout?: () => void;
}

export default function PatientPortalLayout({ children, patientId, profile, activeSection, onSectionChange, onLogout }: PatientPortalLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const sidebarWidth = collapsed ? 64 : 200;

  // If no sidebar nav needed (onboarding, form viewer), render simple layout
  if (!onSectionChange) {
    return (
      <div style={{
        minHeight: "100vh",
        backgroundColor: "#f5f0eb",
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}>
        <Header patientId={patientId} onLogout={onLogout} />
        <main style={{ maxWidth: 720, margin: "0 auto", padding: "32px 20px" }}>
          {children}
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: "100vh",
      backgroundColor: "#f5f0eb",
      fontFamily: "system-ui, -apple-system, sans-serif",
      display: "flex",
      flexDirection: "column",
    }}>
      <Header patientId={patientId} onLogout={onLogout} />

      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Left sidebar */}
        <nav style={{
          width: sidebarWidth,
          flexShrink: 0,
          backgroundColor: "#182a33",
          display: "flex",
          flexDirection: "column",
          transition: "width 0.2s ease",
          overflow: "hidden",
        }}>
          {/* Patient name */}
          {!collapsed && profile?.firstName && (
            <div style={{
              padding: "20px 16px 12px",
              borderBottom: "1px solid rgba(255,255,255,0.1)",
            }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#fff" }}>
                {profile.firstName} {profile.lastName}
              </div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>
                {profile.email}
              </div>
            </div>
          )}

          {/* Nav items */}
          <div style={{ flex: 1, paddingTop: 8 }}>
            {NAV_ITEMS.map(item => {
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => onSectionChange(item.id)}
                  title={collapsed ? item.label : undefined}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: collapsed ? "14px 0" : "12px 16px",
                    justifyContent: collapsed ? "center" : "flex-start",
                    backgroundColor: isActive ? "rgba(255,255,255,0.15)" : "transparent",
                    color: isActive ? "#fff" : "rgba(255,255,255,0.7)",
                    border: "none",
                    borderLeft: isActive ? "3px solid #fff" : "3px solid transparent",
                    cursor: "pointer",
                    fontSize: 14,
                    fontWeight: isActive ? 600 : 400,
                    transition: "all 0.15s",
                    textAlign: "left",
                  }}
                >
                  <span style={{ fontSize: collapsed ? 20 : 16, flexShrink: 0 }}>{item.icon}</span>
                  {!collapsed && item.label}
                </button>
              );
            })}
          </div>

          {/* Collapse toggle */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            style={{
              padding: "12px",
              backgroundColor: "transparent",
              border: "none",
              color: "rgba(255,255,255,0.5)",
              cursor: "pointer",
              fontSize: 16,
              borderTop: "1px solid rgba(255,255,255,0.1)",
              textAlign: "center",
            }}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? "»" : "«"}
          </button>
        </nav>

        {/* Main workspace */}
        <main style={{
          flex: 1,
          overflow: "auto",
          padding: "28px 32px",
          minWidth: 0,
        }}>
          <div style={{ maxWidth: 900, margin: "0 auto" }}>
            {children}
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
}

// Short "About Us" blurb — keep in sync with bridge/mailer.py ABOUT_US. Edit to taste.
const ABOUT_US =
  "Parseo Health helps your care team complete and submit prior-authorization and " +
  "insurance forms quickly and securely. Your information is encrypted and handled " +
  "in accordance with Canadian privacy law (PHIPA / PIPEDA).";

function Footer() {
  return (
    <footer style={{
      flexShrink: 0,
      borderTop: "1px solid #e6dccb",
      backgroundColor: "#f5f0eb",
      padding: "20px 24px",
      color: "#7e8c96",
      fontSize: 12,
      lineHeight: 1.5,
    }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <ParseoLogo size={18} />
          <span style={{ fontWeight: 600, color: "#5b6e7c" }}>About Parseo Health</span>
        </div>
        <div>{ABOUT_US}</div>
        <a href="https://www.parseohealth.com"
           style={{ color: "#1b5e6e", textDecoration: "none", display: "inline-block", marginTop: 8 }}>
          parseohealth.com
        </a>
      </div>
    </footer>
  );
}

interface HeaderProps {
  patientId?: string;
  onLogout?: () => void;
}

function Header({ patientId, onLogout }: HeaderProps) {
  return (
    <header style={{
      backgroundColor: "#182a33",
      color: "white",
      padding: "12px 24px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      flexShrink: 0,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <ParseoLogo size={28} />
        <div style={{ fontSize: 22, fontWeight: 700 }}>Parseo Health</div>
        <span style={{ opacity: 0.7, fontSize: 13 }}>Patient Portal</span>
      </div>
      <nav style={{ display: "flex", gap: 16, fontSize: 14, alignItems: "center" }}>
        {patientId && <NotificationBell patientId={patientId} />}
        {onLogout && (
          <button
            onClick={onLogout}
            style={{
              background: "none",
              border: "1px solid rgba(255,255,255,0.3)",
              color: "white",
              borderRadius: 6,
              padding: "6px 12px",
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Sign out
          </button>
        )}
      </nav>
    </header>
  );
}
